import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useAuth } from 'src/lib/store/auth-store';
import { SplashScreen } from 'src/components/loading-screen';
import { SubscriptionProvider, useSubscription } from 'src/context/subscription-context';
import { useAuthControllerGetOnboardingStatus } from '@/apis/backend/generated/auth/auth';
import { paths } from '@/routes/paths';

export const Route = createFileRoute('/_authed')({
  component: AuthedLayout,
});

function SubscriptionGate({ children }: { children: React.ReactNode }) {
  const { isLoading } = useSubscription();

  if (isLoading) {
    return <SplashScreen />;
  }

  // Free-tier users are allowed through -- per-feature paywalls
  // (UpgradeModal, EmailLimitModal) handle usage limits
  return <>{children}</>;
}

function AuthedLayout() {
  const { state, initComplete } = useAuth();
  const navigate = useNavigate();

  // Check onboarding status for authenticated users
  const { data: onboardingStatus, isLoading: isLoadingOnboarding } =
    useAuthControllerGetOnboardingStatus({
      query: {
        enabled: initComplete && state === 'authenticated',
      },
    });

  useEffect(() => {
    // Only redirect AFTER initialization is complete
    // This prevents redirecting before stored tokens are validated
    if (initComplete && state === 'unauthenticated') {
      navigate({
        to: '/auth/sign-in',
        search: {
          // Carry the query string and the fragment, not just the path. Third-party
          // returns land here unauthenticated with their payload in the query (e.g.
          // Shopify's plan approval sends ?shopify_billing=..., OAuth returns send
          // ?code=...), and dropping it silently strands the flow after login. The
          // fragment is how an email footer deep-links to one card on a settings
          // page, so losing it drops the reader at the top of a long page instead.
          redirect: window.location.pathname + window.location.search + window.location.hash,
          shopify_state: undefined,
          already_connected: undefined,
          error: undefined,
        },
      });
    }
  }, [state, initComplete, navigate]);

  // Redirect to onboarding if incomplete
  useEffect(() => {
    if (onboardingStatus && onboardingStatus.status !== 'completed') {
      navigate({ to: paths.auth.onboarding });
    }
  }, [onboardingStatus, navigate]);

  // Show loading screen while auth is initializing or not fully authenticated
  if (!initComplete || state !== 'authenticated') {
    return <SplashScreen />;
  }

  // Show loading while checking onboarding status
  if (isLoadingOnboarding) {
    return <SplashScreen />;
  }

  return (
    <SubscriptionProvider>
      <SubscriptionGate>
        <Outlet />
      </SubscriptionGate>
    </SubscriptionProvider>
  );
}
